/** Rows of any run: pick the run, see its outcomes as a bar, filter by one, search by key or name. */

import { useState } from "react";
import { Rows3 } from "lucide-react";

import { RunRows } from "@/components/integrations/RunRows";
import { Banner, Button, Card, Column, Empty, Field, FieldRow, OutcomeBar, Panel, Select, SkeletonRows } from "@/components/kit";
import type { IntegrationDetail, Run } from "@/lib/integrations/api";
import { runStatus, viaWords, when } from "@/lib/integrations/words";

import type { Keyed } from "../hooks";

export function RowsTab({ item, runs }: { item: IntegrationDetail; runs: Keyed<Run[]> }) {
  const timeZone = item.eventTimezone ?? "UTC";
  const [picked, setPicked] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  if (!runs.data) {
    return runs.error ? <Banner tone="danger" actions={<Button onClick={runs.reload}>Try again</Button>}>{runs.error}</Banner> : <SkeletonRows rows={5} columns={4} />;
  }
  if (runs.data.length === 0) {
    return (
      <Card>
        <Empty title="No rows yet" note="Rows show here once a run is read or a version is tried." />
      </Card>
    );
  }
  const run = runs.data.find((one) => one.id === picked) ?? runs.data.find((one) => one.via !== "try") ?? runs.data[0];
  return (
    <Column>
      <Panel
        title="What happened to each row"
        icon={<Rows3 size={18} aria-hidden />}
        note="Pick a run. Click an outcome under the bar to see only those rows."
      >
        <FieldRow>
          <Field label="Run">
            <Select
              value={run.id}
              onChange={(event) => {
                setPicked(event.target.value);
                setOutcome(null);
              }}
            >
              {runs.data.map((one) => (
                <option key={one.id} value={one.id}>
                  {when(one.createdAt, timeZone)} · {viaWords(one.via)} · {runStatus(one.status).word} · {one.rowsCollected.toLocaleString()} rows
                </option>
              ))}
            </Select>
          </Field>
        </FieldRow>
        <OutcomeBar counts={run.counts} onPick={setOutcome} label={`${run.rowsCollected.toLocaleString()} rows from Scout:`} />
      </Panel>
      <RunRows key={run.id} code={item.code} runId={run.id} counts={run.counts} outcome={outcome} onOutcome={setOutcome} />
    </Column>
  );
}
